// ABOUTME: Shared Steersman fade-out state (show/hide + toggle + once-per-crossing seen-increment),
// ABOUTME: lifted out of SteersmanStrip so the header toggle and the coaching strip can share one state (Issue #1940).

import { useEffect, useRef, useState } from 'react';
import {
  setSteersmanEnabled, incrementSteersmanSeen,
  setSteersmanEnabledForChild, incrementSteersmanSeenForChild,
} from '../../../services/api';

const STEERSMAN_FADE_THRESHOLD = 3;

interface UseSteersmanPreferenceOptions {
  crossingId: number;
  childId: number;
  seenCount: number;
  override: boolean | null;
  slug?: string;
  token?: string;
}

export function useSteersmanPreference({ crossingId, childId, seenCount, override, slug, token }: UseSteersmanPreferenceOptions) {
  // The grown-up's own toggle wins; until they toggle, the saved preference applies. It loads after the first render, so it
  // is read from the prop each render rather than copied into state, which would show one stale frame when it arrives.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const seenIncrementFired = useRef(false);

  const showSteersman = toggled ?? override ?? seenCount < STEERSMAN_FADE_THRESHOLD;
  const isChildMode = !!slug;

  useEffect(() => {
    if (!showSteersman || seenIncrementFired.current) return;
    const flagKey = `crossing_steersman_seen_${crossingId}`;
    let alreadySeen = false;
    try {
      alreadySeen = !!sessionStorage.getItem(flagKey);
    } catch {
      // sessionStorage unavailable -- fall through and increment anyway rather than never counting.
    }
    if (alreadySeen) {
      seenIncrementFired.current = true;
      return;
    }
    seenIncrementFired.current = true;
    try {
      sessionStorage.setItem(flagKey, '1');
    } catch {
      // Best-effort guard; a double-increment across a lost sessionStorage write is an acceptable edge case.
    }
    if (isChildMode) {
      incrementSteersmanSeenForChild(slug!, crossingId, token);
    } else {
      incrementSteersmanSeen(crossingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossingId, showSteersman]);

  const toggle = () => {
    const next = !showSteersman;
    setToggled(next);
    if (isChildMode) {
      setSteersmanEnabledForChild(slug!, next, token);
    } else {
      setSteersmanEnabled(childId, next);
    }
  };

  return { showSteersman, toggle };
}
